import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, FileText, CheckCircle2, XCircle, Send, Mail, Printer, RotateCcw,
  Trash2, Edit3, Search, FileCheck2, Receipt, Save, Eye,
} from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../theme';
import {
  SectionCard, StatCard, TabBar, StatusPill,
  StickyTopBar, StickyBottomBar, SortableTh, HoverActions,
} from '../components/swipe';

// Bill status / type → StatusPill preset key. Centralised so we can change
// colors in one place.
const STATUS_LABEL = {
  draft:         'Draft',
  finalized:     'Finalized',
  'partial-paid':'Partial Paid',
  paid:          'Paid',
  cancelled:     'Cancelled',
  credited:      'Credited',
};
const TYPE_LABEL = {
  invoice:      'Invoice',
  proforma:     'Proforma',
  'credit-note':'Credit Note',
};

const fmtINR = n => `₹${(Number(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const makeEmptyLine = () => ({
  description: '', itemType: 'service', hsnCode: '', toothNumber: '',
  quantity: 1, unitPrice: 0, discount: 0, discountPercent: 0, gstPercent: 18,
});
const makeEmptyBill = () => ({
  patientId: '', billType: 'invoice', date: new Date().toISOString().slice(0,10),
  lineItems: [makeEmptyLine()],
  billDiscount: 0, billDiscountPct: 0,
  gstMode: 'exclusive', supplyType: 'intra', placeOfSupply: '',
  patientGstin: '', patientLegalName: '', notes: '', terms: '',
  couponCode: '', couponInfo: null,
});

export default function BillingPage({ API_BASE, patientsList, currentUser }) {
  const [tab, setTab] = useState('list');
  const [statusTab, setStatusTab] = useState('all');   // list-view filter row
  const [bills, setBills] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [pharmacyItems,  setPharmacyItems]  = useState([]);
  const [filter, setFilter] = useState({ billType: '', from: '', to: '' });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });

  // Editor state
  const [form, setForm] = useState(makeEmptyBill());
  const [preview, setPreview] = useState(null);
  const [nextNumber, setNextNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type:'', text:'' });
  const [opdStatus, setOpdStatus] = useState(null);

  // Fetch OPD validity for the bill's selected patient.
  useEffect(() => {
    if (!form.patientId) { setOpdStatus(null); return; }
    let cancelled = false;
    axios.get(`${API_BASE}/patients/${form.patientId}/opd-status`)
      .then(r => { if (!cancelled) setOpdStatus(r.data); })
      .catch(() => { if (!cancelled) setOpdStatus(null); });
    return () => { cancelled = true; };
  }, [form.patientId, API_BASE]);

  const fetchBills = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k,v]) => v && params.append(k, v));
      const r = await axios.get(`${API_BASE}/bills?${params.toString()}`);
      setBills(r.data);
    } catch {}
  }, [API_BASE, filter]);

  const fetchMasters = useCallback(async () => {
    try {
      const [tt, ph] = await Promise.all([
        axios.get(`${API_BASE}/treatment-types`).catch(()=>({data:[]})),
        axios.get(`${API_BASE}/pharmacy/items`).catch(()=>({data:[]})),
      ]);
      setTreatmentTypes(tt.data); setPharmacyItems(ph.data);
    } catch {}
  }, [API_BASE]);

  useEffect(() => { fetchBills(); }, [fetchBills]);
  useEffect(() => { fetchMasters(); }, [fetchMasters]);

  useEffect(() => {
    if (tab !== 'editor') return;
    axios.get(`${API_BASE}/bills/next-number?type=${form.billType}`).then(r => setNextNumber(r.data.billNumber)).catch(()=>setNextNumber(''));
  }, [tab, form.billType, API_BASE]);

  // ── Local GST preview (mirrors server-side gstCalculator) ──
  const computeLocal = useCallback(() => {
    const r2 = n => Math.round((Number(n)||0) * 100) / 100;
    const rows = form.lineItems.map(li => {
      const qty = Number(li.quantity) || 1;
      const rate = Number(li.unitPrice) || 0;
      const gross = qty * rate;
      const linePct = Number(li.discountPercent) || 0;
      const lineDisc = linePct > 0 ? gross * linePct / 100 : (Number(li.discount) || 0);
      const afterDisc = Math.max(0, gross - lineDisc);
      const gstPct = Number(li.gstPercent) || 0;
      let taxable, tax;
      if (form.gstMode === 'inclusive' && gstPct > 0) {
        taxable = afterDisc * 100 / (100 + gstPct); tax = afterDisc - taxable;
      } else if (form.gstMode === 'none') { taxable = afterDisc; tax = 0; }
      else { taxable = afterDisc; tax = taxable * gstPct / 100; }
      return { gross, lineDisc, taxable: r2(taxable), gstPct, tax: r2(tax) };
    });
    const taxableBefore = rows.reduce((s,r) => s+r.taxable, 0);
    let billDisc = Number(form.billDiscount) || 0;
    if ((Number(form.billDiscountPct)||0) > 0) billDisc = taxableBefore * (Number(form.billDiscountPct)||0) / 100;
    if (billDisc > taxableBefore) billDisc = taxableBefore;

    const out = rows.map((r, i) => {
      const share = taxableBefore > 0 ? r.taxable / taxableBefore : 0;
      const taxable = r2(r.taxable - billDisc * share);
      const tax = form.gstMode === 'none' ? 0 : r2(taxable * r.gstPct / 100);
      const cgst = form.supplyType === 'intra' ? r2(tax/2) : 0;
      const sgst = form.supplyType === 'intra' ? r2(tax - cgst) : 0;
      const igst = form.supplyType === 'inter' ? tax : 0;
      return { ...form.lineItems[i], taxableAmount: taxable, cgst, sgst, igst, total: r2(taxable + cgst + sgst + igst) };
    });
    const totalTaxable = out.reduce((s,l) => s+l.taxableAmount, 0);
    const totalCgst = out.reduce((s,l) => s+l.cgst, 0);
    const totalSgst = out.reduce((s,l) => s+l.sgst, 0);
    const totalIgst = out.reduce((s,l) => s+l.igst, 0);
    const totalGst = totalCgst + totalSgst + totalIgst;
    const raw = r2(totalTaxable + totalGst);
    const rounded = Math.round(raw);
    return {
      lineItems: out,
      totals: {
        subTotal: r2(rows.reduce((s,r)=>s+r.gross,0)),
        lineDiscount: r2(rows.reduce((s,r)=>s+r.lineDisc,0)),
        billDiscount: r2(billDisc),
        totalDiscount: r2(rows.reduce((s,r)=>s+r.lineDisc,0) + billDisc),
        totalTaxable: r2(totalTaxable), totalCgst: r2(totalCgst), totalSgst: r2(totalSgst), totalIgst: r2(totalIgst), totalGst: r2(totalGst),
        roundOff: r2(rounded - raw),
        totalAmount: rounded,
      },
    };
  }, [form]);

  useEffect(() => { setPreview(computeLocal()); }, [computeLocal]);

  // ── Editor actions ──
  const startNew = (type = 'invoice') => {
    setForm({ ...makeEmptyBill(), billType: type });
    setMsg({ type:'', text:'' });
    setTab('editor');
  };

  const loadFromTemplate = (line, src) => {
    const l = { ...makeEmptyLine() };
    if (src === 'tt') {
      l.description = line.name; l.itemType = 'treatment';
      l.unitPrice = Number(line.defaultPrice) || 0;
      l.quantity  = Number(line.defaultUnits) || 1;
      l.gstPercent = Number(line.gstPercent) || 0;
      l.hsnCode = '998511';
    } else if (src === 'med') {
      l.description = line.name; l.itemType = 'medicine';
      l.unitPrice = Number(line.sellingPrice || line.mrp) || 0;
      l.gstPercent = Number(line.gstPercent) || 0;
      l.hsnCode = line.hsnCode || '';
    }
    setForm(f => ({ ...f, lineItems: [...f.lineItems.filter(x => x.description), l] }));
  };

  const updateLine = (i, patch) => {
    setForm(f => ({ ...f, lineItems: f.lineItems.map((x,idx) => idx===i ? { ...x, ...patch } : x) }));
  };
  const removeLine = i => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_,idx) => idx!==i) }));
  const addLine = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, makeEmptyLine()] }));

  const saveDraft = async () => {
    if (!form.patientId) return setMsg({ type:'error', text:'Select a patient' });
    if (!form.lineItems.some(l => l.description)) return setMsg({ type:'error', text:'At least one line item required' });
    setBusy(true); setMsg({ type:'', text:'' });
    try {
      const payload = { ...form, lineItems: form.lineItems.filter(l => l.description), createdBy: currentUser?.username || '' };
      const r = form._id
        ? await axios.put(`${API_BASE}/bills/${form._id}`, payload)
        : await axios.post(`${API_BASE}/bills/add`, payload);
      const saved = r.data.bill || r.data;
      setMsg({ type:'success', text: form._id ? 'Draft updated' : 'Draft saved' });
      setForm({ ...form, _id: saved._id });
      fetchBills();
    } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Save failed' }); }
    setBusy(false);
  };

  const validateCoupon = async () => {
    if (!form.couponCode) return setMsg({ type:'error', text:'Enter a coupon code' });
    try {
      const lineTypes = [...new Set(form.lineItems.map(l => l.itemType))];
      const r = await axios.post(`${API_BASE}/coupons/validate`, {
        code: form.couponCode, patientId: form.patientId,
        subTotal: preview?.totals?.totalTaxable || 0, lineItemTypes: lineTypes,
      });
      if (r.data.ok) {
        setForm(f => ({ ...f, couponInfo: r.data }));
        setMsg({ type:'success', text:`Coupon valid — ₹${r.data.discount} off` });
      } else {
        setForm(f => ({ ...f, couponInfo: null }));
        setMsg({ type:'error', text: r.data.reason });
      }
    } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Validation failed' }); }
  };

  const callFinalize = async (billId, override = false) => {
    try {
      const r = await axios.put(`${API_BASE}/bills/${billId}/finalize`, {
        finalizedBy: currentUser?.username || '',
        couponCode: form.couponCode || undefined,
        skipCreditCheck: override,
      });
      setMsg({ type:'success', text:`Bill ${r.data.bill.billNumber} finalized` });
      setForm({ ...makeEmptyBill() });
      setTab('list'); fetchBills();
    } catch (e) {
      const err = e.response?.data;
      if (err?.error && /Credit-limit/.test(err.error)) {
        if (window.confirm(`${err.error}\n\nOverride and finalize anyway?`)) {
          return callFinalize(billId, true);
        }
      }
      setMsg({ type:'error', text: err?.error || 'Finalize failed' });
    }
  };

  const finalize = async () => {
    if (!form._id) {
      if (!form.patientId) return setMsg({ type:'error', text:'Select a patient' });
      if (!form.lineItems.some(l => l.description)) return setMsg({ type:'error', text:'At least one line item required' });
      setBusy(true); setMsg({ type:'', text:'' });
      try {
        const r = await axios.post(`${API_BASE}/bills/add`, {
          ...form, lineItems: form.lineItems.filter(l => l.description),
          createdBy: currentUser?.username || '',
        });
        const draftId = r.data.bill._id;
        await callFinalize(draftId);
      } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Finalize failed' }); }
      setBusy(false);
      return;
    }
    if (!window.confirm('Finalize this bill? Number will be locked and editing disabled.')) return;
    setBusy(true);
    await callFinalize(form._id);
    setBusy(false);
  };

  const cancelBill = async (bill) => {
    const reason = window.prompt('Cancellation reason:'); if (reason === null) return;
    try {
      await axios.put(`${API_BASE}/bills/${bill._id}/cancel`, { reason, cancelledBy: currentUser?.username || '' });
      fetchBills();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const sendWA = async (bill) => {
    try { const r = await axios.post(`${API_BASE}/bills/${bill._id}/send-whatsapp`, {}); alert(r.data.success ? 'WhatsApp sent' : 'WA: ' + (r.data.message||'failed')); fetchBills(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const sendEmail = async (bill) => {
    try { const r = await axios.post(`${API_BASE}/bills/${bill._id}/send-email`, {}); alert(r.data.success ? 'Email sent' : 'Email: ' + (r.data.message||'failed')); fetchBills(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const printBill = (bill) => window.open(`${API_BASE}/invoices/generate/${bill._id}`, '_blank');

  const convertProforma = async (bill) => {
    if (!window.confirm('Convert this proforma into a draft invoice?')) return;
    try {
      const r = await axios.post(`${API_BASE}/bills/${bill._id}/convert-to-invoice`, {});
      setForm({ ...r.data.bill, date: new Date(r.data.bill.date).toISOString().slice(0,10) });
      setTab('editor');
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const startCreditNote = async (bill) => {
    if (!window.confirm(`Issue a full credit note for ${bill.billNumber || bill.invoiceNo}?`)) return;
    try {
      const items = (bill.lineItems && bill.lineItems.length) ? bill.lineItems : (bill.items || []).map(x => ({
        description: x.description, quantity: 1, unitPrice: x.amount, gstPercent: 0, hsnCode: '',
      }));
      await axios.post(`${API_BASE}/bills/${bill._id}/credit-note`, {
        items, reason: 'Full credit issued', issuedBy: currentUser?.username || '',
      });
      alert('Credit note issued');
      fetchBills();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const editDraft = (bill) => {
    setForm({ ...bill, date: new Date(bill.date).toISOString().slice(0,10) });
    setTab('editor');
  };

  // Derived: status counts for tab badges + summary stats for the top stat row.
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0,10);
    let todayCollected = 0, outstanding = 0, totalAmt = 0;
    const byStatus = { draft: 0, finalized: 0, paid: 0, 'partial-paid': 0, cancelled: 0, credited: 0 };
    for (const b of bills) {
      totalAmt += Number(b.totalAmount) || 0;
      outstanding += Math.max(0, (Number(b.totalAmount)||0) - (Number(b.paidAmount)||0));
      if (b.date && b.date.slice(0,10) === today) todayCollected += Number(b.paidAmount) || 0;
      if (byStatus[b.status] != null) byStatus[b.status] += 1;
    }
    return { todayCollected, outstanding, totalAmt, byStatus };
  }, [bills]);

  // Filter + search + sort pipeline.
  const visibleBills = useMemo(() => {
    let list = bills;
    if (statusTab !== 'all') {
      list = list.filter(b => statusTab === 'pending'
        ? (b.status === 'finalized' || b.status === 'partial-paid')
        : b.status === statusTab);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        (b.billNumber || '').toLowerCase().includes(q) ||
        (b.invoiceNo  || '').toLowerCase().includes(q) ||
        String(b.totalAmount).includes(q));
    }
    const dirMul = sort.dir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === 'number') return (av - bv) * dirMul;
      return String(av).localeCompare(String(bv)) * dirMul;
    });
  }, [bills, statusTab, search, sort]);

  // Bottom summary chips for the list view.
  const visibleSummary = useMemo(() => {
    const total = visibleBills.reduce((s, b) => s + (Number(b.totalAmount)||0), 0);
    const paid  = visibleBills.reduce((s, b) => s + (Number(b.paidAmount)||0), 0);
    return { total, paid, pending: total - paid };
  }, [visibleBills]);

  const topTabs = [
    { key: 'list',   label: 'All Bills',    icon: <Receipt size={14}/> },
    { key: 'editor', label: form._id ? 'Edit Draft' : 'New Bill', icon: <Edit3 size={14}/> },
  ];
  const statusTabs = [
    { key: 'all',          label: 'All',       count: bills.length },
    { key: 'draft',        label: 'Drafts',    count: stats.byStatus.draft },
    { key: 'pending',      label: 'Pending',   count: stats.byStatus.finalized + stats.byStatus['partial-paid'] },
    { key: 'paid',         label: 'Paid',      count: stats.byStatus.paid },
    { key: 'cancelled',    label: 'Cancelled', count: stats.byStatus.cancelled },
  ];

  return (
    <div>
      {/* ─── PAGE HEADER ─── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
        <div>
          <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.4rem', fontWeight: '700' }}>Billing</h2>
          <p style={{ margin: '2px 0 0', color: theme.textMuted, fontSize: '0.85rem' }}>Invoices, proformas, credit notes — GST compliant</p>
        </div>
        {tab === 'list' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={()=>startNew('proforma')} style={btn(theme.warning)}>
              <FileText size={14}/> New Proforma
            </button>
            <button onClick={()=>startNew('invoice')} style={btn(theme.primary)}>
              <Plus size={14}/> Create Invoice
            </button>
          </div>
        )}
      </div>

      {/* Top-level toggle (List vs Editor) */}
      <TabBar tabs={topTabs} active={tab} onChange={setTab} variant="pills"/>

      {/* Message bar */}
      {msg.text && (
        <div style={{
          padding: '10px 14px', borderRadius: '8px', margin: '14px 0',
          backgroundColor: msg.type==='error' ? '#fef2f2' : '#f0fdf4',
          color: msg.type==='error' ? theme.danger : theme.success,
          fontSize: '0.86rem', fontWeight: '500',
          border: `1px solid ${msg.type==='error' ? '#fecaca' : '#bbf7d0'}`,
        }}>
          {msg.text}
        </div>
      )}

      {/* ═══════════ LIST VIEW ═══════════ */}
      {tab === 'list' && (
        <>
          {/* Stat row — 4 colored cards summarising today's billing health */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', margin: '14px 0' }}>
            <StatCard label="Total Bills"        value={bills.length} tone="navy"    icon={<Receipt size={22}/>} />
            <StatCard label="Outstanding"        value={fmtINR(stats.outstanding)}    tone="danger"  sub="Unpaid balance" />
            <StatCard label="Today's Collection" value={fmtINR(stats.todayCollected)} tone="success" sub="Paid today" />
            <StatCard label="Total Billed"       value={fmtINR(stats.totalAmt)}       tone="primary" sub="All time" />
          </div>

          <SectionCard variant="white" padding="0">
            {/* Status tab strip with count badges (Swipe pattern) */}
            <div style={{ padding: '6px 14px 0' }}>
              <TabBar tabs={statusTabs} active={statusTab} onChange={setStatusTab} variant="underline"/>
            </div>

            {/* Filters row */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '10px 14px 14px' }}>
              <div style={{ flex: '1 1 280px', position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '12px', color: theme.textMuted }}/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by bill number or amount…" style={{ ...inputStyle, paddingLeft: '32px' }}/>
              </div>
              <select value={filter.billType} onChange={e=>setFilter(f=>({...f, billType:e.target.value}))} style={{ ...inputStyle, width: 'auto' }}>
                <option value="">All types</option>
                <option value="invoice">Invoices</option>
                <option value="proforma">Proformas</option>
                <option value="credit-note">Credit Notes</option>
              </select>
              <input type="date" value={filter.from} onChange={e=>setFilter(f=>({...f, from:e.target.value}))} style={{ ...inputStyle, width: 'auto' }}/>
              <input type="date" value={filter.to}   onChange={e=>setFilter(f=>({...f, to:e.target.value}))}   style={{ ...inputStyle, width: 'auto' }}/>
            </div>

            {/* Sortable table with hover-revealed action buttons */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <SortableTh label="Number"   field="billNumber"   sortKey={sort.key} sortDir={sort.dir} onSort={(f,d)=>setSort({key:f, dir:d})}/>
                    <SortableTh label="Date"     field="date"         sortKey={sort.key} sortDir={sort.dir} onSort={(f,d)=>setSort({key:f, dir:d})}/>
                    <SortableTh label="Type"/>
                    <SortableTh label="Patient"/>
                    <SortableTh label="Amount"   field="totalAmount"  sortKey={sort.key} sortDir={sort.dir} onSort={(f,d)=>setSort({key:f, dir:d})} align="right"/>
                    <SortableTh label="Paid"     field="paidAmount"   sortKey={sort.key} sortDir={sort.dir} onSort={(f,d)=>setSort({key:f, dir:d})} align="right"/>
                    <SortableTh label="Balance"  field="balanceAmount" sortKey={sort.key} sortDir={sort.dir} onSort={(f,d)=>setSort({key:f, dir:d})} align="right"/>
                    <SortableTh label="Status"/>
                    <SortableTh label=""/>
                  </tr>
                </thead>
                <tbody>
                  {visibleBills.map(b => {
                    const pt = patientsList.find(p => p._id === b.patientId);
                    const isFinal = ['finalized','partial-paid','paid'].includes(b.status);
                    const initials = (pt?.name || '?').split(' ').map(s=>s[0]).slice(0,2).join('').toUpperCase();
                    const actions = [];
                    if (b.status === 'draft') actions.push({ key:'edit',  label:'Edit',     icon:<Edit3 size={12}/>,  color: theme.info + '22',    textColor: theme.info,    onClick: ()=>editDraft(b) });
                    if (b.billType === 'proforma' && isFinal) actions.push({ key:'cv', label:'To Invoice', icon:<FileCheck2 size={12}/>, color: theme.primary + '22', textColor: theme.primary, onClick: ()=>convertProforma(b) });
                    if (isFinal) actions.push({ key:'view',  label:'View',     icon:<Eye size={12}/>,    color: '#e0f2fe',           textColor: '#0369a1',     onClick: ()=>printBill(b) });
                    if (isFinal) actions.push({ key:'wa',    label:'WhatsApp', icon:<Send size={12}/>,   color: '#dcfce7',           textColor: '#15803d',     onClick: ()=>sendWA(b) });
                    if (isFinal) actions.push({ key:'mail',  label:'Email',    icon:<Mail size={12}/>,   color: '#e0e7ff',           textColor: '#3730a3',     onClick: ()=>sendEmail(b) });
                    if (isFinal) actions.push({ key:'print', label:'Print',    icon:<Printer size={12}/>,color: '#f1f5f9',           textColor: '#475569',     onClick: ()=>printBill(b) });
                    if (isFinal && b.billType !== 'credit-note' && b.status !== 'credited') actions.push({ key:'cn', label:'Credit Note', icon:<RotateCcw size={12}/>, color: '#ede9fe', textColor: '#6d28d9', onClick: ()=>startCreditNote(b) });
                    if (b.status !== 'cancelled' && b.status !== 'credited') actions.push({ key:'cancel', label:'Cancel', icon:<XCircle size={12}/>, color: '#fee2e2', textColor: theme.danger, onClick: ()=>cancelBill(b) });
                    return (
                      <tr key={b._id} className="hov-row">
                        <td style={td}><strong style={{ fontSize: '0.84rem', color: theme.navy }}>{b.billNumber || b.invoiceNo || '—'}</strong></td>
                        <td style={td}>{fmtDate(b.date)}</td>
                        <td style={td}>
                          <StatusPill status={b.billType} preset={b.billType} label={TYPE_LABEL[b.billType] || b.billType} size="sm"/>
                          {b.gstStatus === 'exempt' && (
                            <span title="Healthcare composite supply — GST NIL vide Notification 12/2017-CT(R) Entry 74"
                              style={{ marginLeft: 6, fontSize: '0.62rem', fontWeight: 800, color: '#065f46',
                                       background: '#d1fae5', padding: '2px 6px', borderRadius: 999, border: '1px solid #6ee7b7' }}>
                              GST EXEMPT
                            </span>
                          )}
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={avatar(initials)}>{initials}</div>
                            <div>
                              <div style={{ fontWeight: '600', color: theme.navy, fontSize: '0.86rem' }}>{pt?.name || '—'}</div>
                              {pt?.phone && <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{pt.phone}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}><strong>{fmtINR(b.totalAmount)}</strong></td>
                        <td style={{ ...td, textAlign: 'right', color: theme.success }}>{fmtINR(b.paidAmount)}</td>
                        <td style={{ ...td, textAlign: 'right', color: (b.totalAmount - b.paidAmount) > 0 ? theme.danger : theme.textMuted }}>{fmtINR((Number(b.totalAmount)||0) - (Number(b.paidAmount)||0))}</td>
                        <td style={td}><StatusPill status={b.status} preset={b.status} label={STATUS_LABEL[b.status] || b.status}/></td>
                        <td style={{ ...td, textAlign: 'right' }}><HoverActions actions={actions} compact/></td>
                      </tr>
                    );
                  })}
                  {visibleBills.length === 0 && (
                    <tr><td colSpan="9" style={{ ...td, textAlign: 'center', color: theme.textMuted, padding: '50px' }}>No bills match the current filters</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom summary chips — shows totals of currently-filtered rows */}
            {visibleBills.length > 0 && (
              <div style={{
                display: 'flex', gap: '10px', padding: '12px 14px',
                borderTop: `1px solid ${theme.border}`, backgroundColor: '#fafbfc',
                borderRadius: `0 0 ${theme.radiusLg} ${theme.radiusLg}`,
              }}>
                <SummaryChip label="Total"   value={fmtINR(visibleSummary.total)}   tone="navy"/>
                <SummaryChip label="Paid"    value={fmtINR(visibleSummary.paid)}    tone="success"/>
                <SummaryChip label="Pending" value={fmtINR(visibleSummary.pending)} tone="danger"/>
                <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '0.78rem', color: theme.textMuted }}>
                  Showing {visibleBills.length} of {bills.length}
                </div>
              </div>
            )}
          </SectionCard>
        </>
      )}

      {/* ═══════════ EDITOR (CREATE / EDIT BILL) ═══════════ */}
      {tab === 'editor' && (
        <>
          <StickyTopBar
            onBack={()=>{ setForm(makeEmptyBill()); setTab('list'); }}
            title={form._id ? 'Edit Bill' : 'Create Bill'}
            subtitle={form.billType?.toUpperCase()}
            left={
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select value={form.billType} onChange={e=>setForm(f=>({...f, billType:e.target.value}))} style={{ ...inputStyle, width: 'auto', fontWeight: '600' }}>
                  <option value="invoice">INV-</option>
                  <option value="proforma">PRO-</option>
                </select>
                <div style={{ ...inputStyle, width: 'auto', backgroundColor: '#f8fafc', fontFamily: 'monospace', fontSize: '0.84rem', minWidth: '90px', textAlign: 'center' }}>{nextNumber || '—'}</div>
              </div>
            }
            right={
              <>
                <button onClick={saveDraft} disabled={busy} style={btnGhost()}>Save as Draft</button>
                <button onClick={()=>{ saveDraft().then(()=>setTimeout(()=>printBill({_id:form._id}), 800)); }} disabled={busy} style={btnGhost()}>
                  <Printer size={14}/> Save and Print
                </button>
                <button onClick={finalize} disabled={busy} style={btn(theme.primary)}>
                  <CheckCircle2 size={14}/> Finalize {/* arrow */} →
                </button>
              </>
            }
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '14px' }}>
            {/* LEFT: form sections */}
            <div>
              {/* CUSTOMER + DATE + DOC info — pastel blue */}
              <SectionCard variant="blue" bordered title="Customer & Document" subtitle="Patient, date and tax mode for this bill">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Patient *</label>
                    <select value={form.patientId} onChange={e=>setForm(f=>({...f, patientId:e.target.value}))} style={inputStyle}>
                      <option value="">Select patient…</option>
                      {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
                    </select>
                    {opdStatus?.enabled && form.patientId && (
                      opdStatus.valid ? (
                        <div style={{ marginTop: '6px', padding: '6px 10px', fontSize: '0.78rem', backgroundColor: '#e8f7ef', border: '1px solid #c3e6cb', borderRadius: '6px', color: '#155724' }}>
                          ✓ OPD valid till <strong>{new Date(opdStatus.validTill).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                        </div>
                      ) : (
                        <div style={{ marginTop: '6px', padding: '6px 10px', fontSize: '0.78rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', color: '#856404' }}>
                          ⚠️ OPD expired — <strong>₹{opdStatus.fee}</strong> "{opdStatus.label}" finalize par auto-add ho jayega
                        </div>
                      )
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}>Date</label>
                    <input type="date" value={form.date} onChange={e=>setForm(f=>({...f, date:e.target.value}))} style={inputStyle}/>
                  </div>
                  <div>
                    <label style={labelStyle}>GST Mode</label>
                    <select value={form.gstMode} onChange={e=>setForm(f=>({...f, gstMode:e.target.value}))} style={inputStyle}>
                      <option value="exclusive">Exclusive</option>
                      <option value="inclusive">Inclusive</option>
                      <option value="none">None / Exempt</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Supply Type</label>
                    <select value={form.supplyType} onChange={e=>setForm(f=>({...f, supplyType:e.target.value}))} style={inputStyle}>
                      <option value="intra">Intra-state (CGST+SGST)</option>
                      <option value="inter">Inter-state (IGST)</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Place of Supply</label>
                    <input value={form.placeOfSupply} onChange={e=>setForm(f=>({...f, placeOfSupply:e.target.value}))} placeholder="RJ" style={inputStyle}/>
                  </div>
                  <div>
                    <label style={labelStyle}>Patient GSTIN (B2B)</label>
                    <input value={form.patientGstin} onChange={e=>setForm(f=>({...f, patientGstin:e.target.value}))} style={inputStyle}/>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Bill-to legal name (B2B)</label>
                    <input value={form.patientLegalName} onChange={e=>setForm(f=>({...f, patientLegalName:e.target.value}))} style={inputStyle}/>
                  </div>
                </div>
              </SectionCard>

              {/* LINE ITEMS — white card, the workhorse section */}
              <SectionCard
                variant="white"
                title="Products & Services"
                subtitle="Add treatments, medicines or custom line items"
                right={
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <select onChange={e => { if (e.target.value) { const tt = treatmentTypes.find(x=>x._id===e.target.value); if (tt) loadFromTemplate(tt,'tt'); e.target.value=''; } }} defaultValue="" style={{ ...inputStyle, width: 'auto', fontSize: '0.82rem' }}>
                      <option value="">+ Treatment</option>
                      {treatmentTypes.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                    </select>
                    {pharmacyItems.length > 0 && (
                      <select onChange={e => { if (e.target.value) { const m = pharmacyItems.find(x=>x._id===e.target.value); if (m) loadFromTemplate(m,'med'); e.target.value=''; } }} defaultValue="" style={{ ...inputStyle, width: 'auto', fontSize: '0.82rem' }}>
                        <option value="">+ Medicine</option>
                        {pharmacyItems.slice(0,100).map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                      </select>
                    )}
                    <button onClick={addLine} style={btnSm(theme.primary)}>+ Blank Row</button>
                  </div>
                }
              >
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                      <tr>
                        <th style={th}>Description</th>
                        <th style={th}>Tooth</th>
                        <th style={th}>HSN</th>
                        <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                        <th style={{ ...th, textAlign: 'right' }}>Rate</th>
                        <th style={{ ...th, textAlign: 'right' }}>GST%</th>
                        <th style={{ ...th, textAlign: 'right' }}>Total</th>
                        <th style={th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.lineItems.map((l, i) => {
                        const p = preview?.lineItems[i] || {};
                        return (
                          <tr key={i}>
                            <td style={tdEditor}><input value={l.description} onChange={e=>updateLine(i,{description:e.target.value})} style={editorInput}/></td>
                            <td style={tdEditor}><input value={l.toothNumber} onChange={e=>updateLine(i,{toothNumber:e.target.value})} style={{ ...editorInput, width: '60px' }}/></td>
                            <td style={tdEditor}><input value={l.hsnCode} onChange={e=>updateLine(i,{hsnCode:e.target.value})} style={{ ...editorInput, width: '90px', fontSize: '0.78rem' }}/></td>
                            <td style={tdEditor}><input type="number" value={l.quantity} onChange={e=>updateLine(i,{quantity:e.target.value})} style={{ ...editorInput, width: '60px', textAlign: 'right' }}/></td>
                            <td style={tdEditor}><input type="number" value={l.unitPrice} onChange={e=>updateLine(i,{unitPrice:e.target.value})} style={{ ...editorInput, width: '90px', textAlign: 'right' }}/></td>
                            <td style={tdEditor}><input type="number" value={l.gstPercent} onChange={e=>updateLine(i,{gstPercent:e.target.value})} style={{ ...editorInput, width: '60px', textAlign: 'right' }}/></td>
                            <td style={{ ...tdEditor, textAlign: 'right', fontWeight: '700', color: theme.navy }}>{fmtINR(p.total)}</td>
                            <td style={tdEditor}>
                              <button onClick={()=>removeLine(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.danger, padding: '4px' }}><Trash2 size={14}/></button>
                            </td>
                          </tr>
                        );
                      })}
                      {form.lineItems.length === 0 && (
                        <tr><td colSpan="8" style={{ ...tdEditor, textAlign: 'center', color: theme.textMuted, padding: '30px' }}>No items yet — add one above</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              {/* COUPON — pastel cream (attention) */}
              <SectionCard variant="cream" bordered title="🎟 Discount Coupon" subtitle="Manual discounts disabled — only valid coupon codes apply at finalize">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={form.couponCode}
                    onChange={e=>setForm(f=>({...f, couponCode:e.target.value.toUpperCase(), couponInfo:null}))}
                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                    placeholder="Enter coupon code"
                  />
                  <button onClick={validateCoupon} style={btn(theme.warning)}>Validate</button>
                </div>
                {form.couponInfo?.ok && (
                  <div style={{ marginTop: '8px', fontSize: '0.82rem', color: theme.success, fontWeight: '600' }}>
                    ✓ {form.couponInfo.coupon.name || form.couponInfo.coupon.code}: ₹{form.couponInfo.discount} off — applies on finalize
                    {form.couponInfo.capped && (
                      <div style={{ color: '#92400e', fontWeight: '500', marginTop: '2px' }}>
                        ⚠ Coupon face value was ₹{form.couponInfo.faceValue} — capped at {form.couponInfo.capPct}% of this bill.
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>

              {/* NOTES — plain white */}
              <SectionCard variant="white" title="Notes & Terms">
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Notes</label>
                    <textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f, notes:e.target.value}))} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Anything you'd like to say to the patient…"/>
                  </div>
                  <div>
                    <label style={labelStyle}>Terms & Conditions</label>
                    <textarea rows={2} value={form.terms} onChange={e=>setForm(f=>({...f, terms:e.target.value}))} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Optional — payment terms, warranty, etc."/>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* RIGHT: live totals — pastel mint (money) */}
            <div>
              <SectionCard variant="mint" bordered title="Live Summary" style={{ position: 'sticky', top: '88px' }}>
                {preview && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem' }}>
                    <Row label="Sub Total" val={fmtINR(preview.totals.subTotal)}/>
                    {preview.totals.lineDiscount > 0 && <Row label="Line Discount" val={`− ${fmtINR(preview.totals.lineDiscount)}`} muted/>}
                    {preview.totals.billDiscount > 0 && <Row label="Bill Discount" val={`− ${fmtINR(preview.totals.billDiscount)}`} muted/>}
                    <Row label="Taxable Amount" val={fmtINR(preview.totals.totalTaxable)}/>
                    {form.supplyType === 'intra' ? (
                      <>
                        <Row label="CGST" val={fmtINR(preview.totals.totalCgst)}/>
                        <Row label="SGST" val={fmtINR(preview.totals.totalSgst)}/>
                      </>
                    ) : (
                      <Row label="IGST" val={fmtINR(preview.totals.totalIgst)}/>
                    )}
                    {preview.totals.roundOff !== 0 && <Row label="Round Off" val={preview.totals.roundOff.toFixed(2)} muted/>}
                    <div style={{ borderTop: `2px dashed ${theme.sectionMintBorder}`, paddingTop: '10px', marginTop: '6px' }}>
                      <Row label="TOTAL" val={fmtINR(preview.totals.totalAmount)} big/>
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* Tip — pastel lilac */}
              <SectionCard variant="lilac" bordered>
                <div style={{ fontSize: '0.78rem', color: theme.textMuted, lineHeight: '1.5' }}>
                  <strong style={{ color: theme.navy }}>💡 Tip:</strong> "Save as Draft" keeps the bill editable. "Finalize" locks it and assigns the official series number ({nextNumber || 'next available'}). Credit notes, WhatsApp and email delivery become available after finalizing.
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Sticky bottom — keep Save accessible without scrolling */}
          <StickyBottomBar
            left={preview ? `Items: ${form.lineItems.filter(l=>l.description).length} · Total: ${fmtINR(preview.totals.totalAmount)}` : null}
            right={
              <>
                <button onClick={()=>{ setForm(makeEmptyBill()); setTab('list'); }} style={btnGhost()}>Cancel</button>
                <button onClick={saveDraft} disabled={busy} style={btnGhost()}>
                  <Save size={14}/> {form._id ? 'Update Draft' : 'Save Draft'}
                </button>
                <button onClick={finalize} disabled={busy} style={btn(theme.success)}>
                  <CheckCircle2 size={14}/> Finalize & Number
                </button>
              </>
            }
          />
        </>
      )}
    </div>
  );
}

// ── Local style helpers ──
const td = { padding: '11px 14px', color: theme.navy, borderBottom: `1px solid ${theme.borderLight}`, fontSize: '0.86rem', verticalAlign: 'middle' };
const th = { padding: '11px 14px', borderBottom: `1px solid ${theme.border}`, color: theme.textMuted, textAlign: 'left', fontSize: '0.74rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', backgroundColor: '#fafbfc', whiteSpace: 'nowrap' };
const tdEditor = { padding: '6px 6px', borderBottom: `1px solid ${theme.borderLight}`, verticalAlign: 'middle' };
const editorInput = { padding: '8px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`, fontSize: '0.84rem', width: '100%', boxSizing: 'border-box', backgroundColor: theme.surface, color: theme.navy };

const btn = (bg) => ({
  padding: '9px 16px', backgroundColor: bg, color: '#fff', border: 'none',
  borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem',
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  boxShadow: `0 1px 2px ${bg}40`, transition: 'transform 0.1s',
});
const btnGhost = () => ({
  padding: '9px 14px', backgroundColor: theme.surface, color: theme.navy,
  border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer',
  fontWeight: '600', fontSize: '0.84rem', display: 'inline-flex', alignItems: 'center', gap: '6px',
});
const btnSm = (bg) => ({
  padding: '7px 12px', backgroundColor: bg, color: '#fff', border: 'none',
  borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem',
});

const avatar = (initials) => {
  // Deterministic color from the initials so the same patient always gets
  // the same avatar tint — purely cosmetic, helps users scan long lists.
  const palette = ['#dbeafe','#dcfce7','#fef3c7','#fce7f3','#e0e7ff','#fed7aa','#cffafe','#fef9c3'];
  const idx = (initials.charCodeAt(0) || 0) % palette.length;
  return {
    width: '32px', height: '32px', borderRadius: '50%',
    backgroundColor: palette[idx],
    color: theme.navy, fontWeight: '700', fontSize: '0.74rem',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };
};

const SummaryChip = ({ label, value, tone }) => {
  const toneMap = { navy: { bg: '#f1f5f9', fg: theme.navy }, success: { bg: '#dcfce7', fg: '#15803d' }, danger: { bg: '#fee2e2', fg: '#991b1b' } };
  const t = toneMap[tone] || toneMap.navy;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '8px', backgroundColor: t.bg }}>
      <span style={{ fontSize: '0.74rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>{label}</span>
      <strong style={{ color: t.fg, fontSize: '0.95rem' }}>{value}</strong>
    </div>
  );
};

const Row = ({ label, val, muted, big }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
    <span style={{ color: muted ? theme.textMuted : theme.textMuted, fontSize: big ? '0.95rem' : '0.85rem', fontWeight: big ? '700' : '500' }}>{label}</span>
    <strong style={{ color: big ? theme.success : (muted ? theme.textMuted : theme.navy), fontSize: big ? '1.25rem' : '0.9rem' }}>{val}</strong>
  </div>
);
